
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertTriangle, Clock, CheckCircle, User, Bot, RefreshCw, ChevronDown, Code2, Server, Database } from 'lucide-react';
import { useErrorReporting } from '@/hooks/useErrorReporting';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { getHttpStatusCategory } from '@/lib/errorParser';

export default function CRMErrorReports() {
  const { reports, loading, fetchReports, generateAISolution, updateReportStatus } = useErrorReporting();
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [generatingAI, setGeneratingAI] = useState<string | null>(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'destructive';
      case 'in_progress':
        return 'secondary';
      case 'resolved':
        return 'default';
      default:
        return 'outline';
    }
  };

  const handleGenerateAI = async (report: any) => {
    setGeneratingAI(report.id);
    try {
      await generateAISolution(report.id, report.message, report.context);
    } finally {
      setGeneratingAI(null);
    }
  };

  const handleStatusUpdate = async (reportId: string, newStatus: string) => {
    await updateReportStatus(reportId, newStatus);
  };

  if (loading && reports.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-foreground">Foutmeldingen</h1>
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-muted rounded-lg h-32"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Foutmeldingen</h1>
        <Button onClick={fetchReports} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Vernieuwen
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Reports List */}
        <div className="lg:col-span-5 space-y-4">
          {reports.length === 0 ? (
            <Card>
              <CardContent className="text-center py-8">
                <CheckCircle className="mx-auto h-12 w-12 text-green-500 mb-4" />
                <h3 className="text-lg font-medium mb-2">Geen foutmeldingen</h3>
                <p className="text-muted-foreground">
                  Er zijn momenteel geen openstaande foutmeldingen.
                </p>
              </CardContent>
            </Card>
          ) : (
            reports.map((report) => (
              <Card
                key={report.id}
                className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                  selectedReport?.id === report.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setSelectedReport(report)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(report.status)}
                      <Badge variant={getStatusColor(report.status) as any}>
                        {report.status === 'open' && 'Open'}
                        {report.status === 'in_progress' && 'In behandeling'}
                        {report.status === 'resolved' && 'Opgelost'}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(report.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                    </span>
                  </div>
                  <CardTitle className="text-sm line-clamp-2">{report.message}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <div>Pagina: {report.path}</div>
                    {report.suggested_fix && (
                      <div className="flex items-center gap-1">
                        <Bot className="h-3 w-3" />
                        <span>AI-oplossing beschikbaar</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Report Details */}
        <div className="lg:col-span-7">
          {selectedReport ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {getStatusIcon(selectedReport.status)}
                    Foutmelding Details
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select
                      value={selectedReport.status}
                      onValueChange={(value) => handleStatusUpdate(selectedReport.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In behandeling</SelectItem>
                        <SelectItem value="resolved">Opgelost</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2">Foutmelding</h4>
                  <div className="bg-muted p-3 rounded-md">
                    <code className="text-sm">{selectedReport.message}</code>
                  </div>
                </div>

                {/* New detailed error information */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedReport.error_code && (
                    <div>
                      <span className="font-medium flex items-center gap-2">
                        <Code2 className="h-4 w-4" />
                        Error Code:
                      </span>
                      <Badge variant="destructive" className="mt-1">
                        {selectedReport.error_code}
                      </Badge>
                    </div>
                  )}
                  
                  {selectedReport.http_status && (
                    <div>
                      <span className="font-medium flex items-center gap-2">
                        <Server className="h-4 w-4" />
                        HTTP Status:
                      </span>
                      <Badge 
                        variant={
                          getHttpStatusCategory(selectedReport.http_status) === 'server_error' ? 'destructive' :
                          getHttpStatusCategory(selectedReport.http_status) === 'client_error' ? 'secondary' : 'default'
                        }
                        className="mt-1"
                      >
                        {selectedReport.http_status}
                      </Badge>
                    </div>
                  )}

                  {selectedReport.function_name && (
                    <div>
                      <span className="font-medium flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        Edge Function:
                      </span>
                      <div className="text-muted-foreground mt-1 font-mono text-sm">
                        {selectedReport.function_name}
                      </div>
                    </div>
                  )}
                </div>

                {selectedReport.supabase_hint && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="font-medium text-blue-900 mb-1">💡 Supabase Hint</div>
                    <div className="text-sm text-blue-800">{selectedReport.supabase_hint}</div>
                  </div>
                )}

                {selectedReport.error_details && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 font-medium hover:underline">
                      <ChevronDown className="h-4 w-4" />
                      Error Details
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                        <code className="text-xs">
                          {typeof selectedReport.error_details === 'string' 
                            ? selectedReport.error_details 
                            : JSON.stringify(selectedReport.error_details, null, 2)}
                        </code>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {selectedReport.request_body && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 font-medium hover:underline">
                      <ChevronDown className="h-4 w-4" />
                      Request Body
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                        <code className="text-xs">
                          {JSON.stringify(selectedReport.request_body, null, 2)}
                        </code>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {selectedReport.response_body && (
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 font-medium hover:underline">
                      <ChevronDown className="h-4 w-4" />
                      Response Body
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                        <code className="text-xs">
                          {typeof selectedReport.response_body === 'string' 
                            ? selectedReport.response_body 
                            : JSON.stringify(selectedReport.response_body, null, 2)}
                        </code>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Pagina:</span>
                    <div className="text-muted-foreground">{selectedReport.path}</div>
                  </div>
                  <div>
                    <span className="font-medium">Datum:</span>
                    <div className="text-muted-foreground">
                      {format(new Date(selectedReport.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                    </div>
                  </div>
                </div>

                {selectedReport.stack && (
                  <div>
                    <h4 className="font-medium mb-2">Stack Trace</h4>
                    <div className="bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                      <code className="text-xs whitespace-pre-wrap">{selectedReport.stack}</code>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Context</h4>
                  <div className="bg-muted p-3 rounded-md max-h-40 overflow-y-auto">
                    <code className="text-xs">
                      {JSON.stringify(selectedReport.context, null, 2)}
                    </code>
                  </div>
                </div>

                {selectedReport.suggested_fix ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="h-4 w-4 text-blue-500" />
                      <h4 className="font-medium">AI-gegenereerde Oplossing</h4>
                      <Badge variant="outline" className="text-xs">
                        {selectedReport.ai_model}
                      </Badge>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-md">
                      <div className="whitespace-pre-wrap text-sm">{selectedReport.suggested_fix}</div>
                    </div>
                    {selectedReport.last_ai_run_at && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Laatst gegenereerd: {format(new Date(selectedReport.last_ai_run_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button
                      onClick={() => handleGenerateAI(selectedReport)}
                      disabled={generatingAI === selectedReport.id}
                      className="w-full"
                    >
                      <Bot className={`mr-2 h-4 w-4 ${generatingAI === selectedReport.id ? 'animate-spin' : ''}`} />
                      {generatingAI === selectedReport.id ? 'AI-oplossing genereren...' : 'AI-oplossing genereren'}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Laat AI een mogelijke oplossing voorstellen voor deze fout
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <User className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Selecteer een foutmelding</h3>
                <p className="text-muted-foreground">
                  Kies een foutmelding uit de lijst om de details te bekijken.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
